import React, { useState, useReducer, useEffect } from 'react';
import { useIntl } from 'umi';
import { Button, Modal, Radio, InputNumber, Form, RadioGroup, Checkbox, Switch, notification } from 'tea-component';
import { buildGenresReqParams, getSortKey, getDateRangeByYears } from '../../utils';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { notificationDownloadError } from '@intelligence/services/download';
import { PAGE_LOG_CONFIG_KEYS } from '../../constants';
import { IOptions, IAction } from '../../types';
import { downloadGlobalViewData } from '@intelligence/services/globalView';
import { getBasePath } from '@/utils/utils';

// import TipContext from '@home/components/TipContextComponents/TipContext';
import './index.scss';
import { navType } from '@home/components/NavHeader/_config';

const PLATFORM_MAP = [
  {
    text: 'dbi_global_view_total_market',
    value: 'total',
  },
  {
    text: 'dbi_mobile_games',
    value: 'mobile',
  },
  {
    text: 'dbi_pc_games',
    value: 'pc',
  },
  {
    text: 'dbi_console_games',
    value: 'console',
  },
];

const TIME_SETTINGS_MAP = [
  {
    text: 'dbi_label_aggregated',
    value: 'aggregated',
  },
  {
    text: 'dbi_label_separate_by_time',
    value: 'separate',
  },
];

const MARKET_SETTINGS_MAP = [
  {
    text: 'dbi_global_view_region',
    value: 'region',
  },
  {
    text: 'dbi_global_view_core_markets',
    value: 'market',
  },
];

const OHTER_SETTINGS_MAP = [
  {
    text: 'dbi_export_all_data',
    value: '0',
  },
  {
    text: 'dbi_select_top',
    value: '1',
  },
];

const FILE_FORMAT_MAP = [
  {
    text: 'Excel',
    value: '0',
  },
  {
    text: 'CSV',
    value: '1',
  },
];

const optionReducer = (state: IOptions, { type, payload }: IAction) => ({
  ...state,
  [type]: payload,
});

const getDefaultSelectTop = (total: any): number => {
  const totalNum = Number.parseInt(total, 10);
  if (Number.isNaN(totalNum)) return 20;
  return Math.min(total, 20);
};

export default function DownloadModal(props: any) {
  const { onClose, isOpen, isDodHide, perType, revenueType, total, filter, tableAction, regionOpts, marketOpts, isNormalAllGenre } = props;

  // console.log('DownloadModal', props);

  const defaultSelectTop = getDefaultSelectTop(total);
  const { formatMessage } = useIntl();
  const [isDownloading, setIsDownLoading] = useState(false);
  const [options, optionsDispatch] = useReducer(optionReducer, {
    platform: [revenueType],
    timeSettings: 'aggregated',
    marketSettings: [filter.regionType],
    ratio: true,
    selectTop: '0',
    amoutNumber: defaultSelectTop,
    fileFormat: '0',
  });

  const handleChange = ({ type, payload }: IAction) => {
    optionsDispatch({ type, payload });
  };

  const getParams = () => {
    const [startDate, endDate] = getDateRangeByYears({ revenueType, filter });
    const sortedPlatforms = options?.platform?.sort((a: string, b: string) => {
      const aIdx = PLATFORM_MAP.findIndex((item) => item?.value === a);
      const bIdx = PLATFORM_MAP.findIndex((item) => item?.value === b);
      return aIdx - bIdx;
    });
    const revenueTypeToTabName = {
      'mobile': 'Mobile Games',
      'pc': 'PC Games',
      'console': 'Console Games',
      'total': 'Total Market',
    } as any;
    const mainTabName = perType === 'genre' ? 'Top Market per Genre' : 'Top Genre per Market';
    const subTabName = revenueTypeToTabName[revenueType] || 'Total Market';

    let paramsVlaue = {
      per_type: perType,
      region_type: options?.marketSettings,
      genre_type: filter?.genreType,
      genre: buildGenresReqParams({ perType, filter }),
      order_metrc: getSortKey(tableAction?.sortKey),
      order: tableAction.sortOrder,
      start_date: startDate,
      end_date: endDate,
      metrics: ['revenue', 'download'],
      period: 'month',
      download_metrics: sortedPlatforms,
      downlaod_date_method: options.timeSettings,
      ratio_show: isDodHide ? 0 : (options.ratio ? 1 : 0),
      select_top: options.selectTop === '0' ? 0 : options.amoutNumber,  // 应后端要求，选Export all data，固定传0
      file_format: Number(options.fileFormat),
      new_style_donwload_file_name: `Global View_${mainTabName}_${subTabName}`,
      download_tab: revenueType,
    } as any;

    // 当下载时region或者market是全选时，传给后端的值为[]
    const getRealRegions = (values: string[], options: any) => values?.length === options.length ? [] : values;

    if (perType === 'market') {
      paramsVlaue = {
        ...paramsVlaue,
        is_region: filter.regionType === 'region' ? true : false,
        regions: filter.regionType === 'region' ? getRealRegions(filter.regions, regionOpts) : getRealRegions(filter.markets, marketOpts),
      };
    } else {
      paramsVlaue = {
        ...paramsVlaue,
        is_select_all: isNormalAllGenre
      };
    }

    return paramsVlaue;
  };

  // const download = () => {
  //   downloadGlobalViewData(getParams());
  // };

  const logDownload = () => {
    const pageKey = `${perType}_${revenueType}`;
    const pageLogConfigKey = PAGE_LOG_CONFIG_KEYS[pageKey];
    const downloadLogConfigKey = `${pageLogConfigKey}Download`;
    // @ts-ignore;
    const downloadLogConfig = dbiBtnLogMes[downloadLogConfigKey];
    if (!downloadLogConfig) return;
    buriedBtnPoint(downloadLogConfig);
  };

  const getMarketDisable = (marketName: string) => {
    if (perType === 'market' && filter.regionType === 'region' && marketName == 'market') {
      return true;
    } else if (perType === 'market' && filter.regionType === 'market' && marketName == 'region') {
      return true;
    } else {
      return false;
    }
  };

  const download = async () => {
    setIsDownLoading(true);
    try {
      const res = await downloadGlobalViewData(getParams());
      const { task_id: taskId } = res?.data || {};
      // 缺失taskId throw异常，走catch
      if (!taskId) throw new Error('missing taskId when download.');
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set('entityType', encodeURIComponent('mobile,pc,console'));
      urlSearchParams.set('taskId', encodeURIComponent(taskId));
      window.open(encodeURI(`${getBasePath()}/intelligence/download?${urlSearchParams.toString()}`));
    } catch (exception: any) {
      notificationDownloadError({
        code: exception?.code,
        msg: exception?.message || 'unknow exception occured when download.',
      });
    } finally {
      setIsDownLoading(false);
    }
    logDownload();
    onClose();
    reset();
  };

  const reset = () => {
    optionsDispatch({ type: 'platform', payload: [revenueType] });
    optionsDispatch({ type: 'timeSettings', payload: 'aggregated' });
    optionsDispatch({ type: 'marketSettings', payload: [filter.regionType] });
    optionsDispatch({ type: 'ratio', payload: true });
    optionsDispatch({ type: 'selectTop', payload: '0' });
    optionsDispatch({ type: 'amoutNumber', payload: defaultSelectTop });
    optionsDispatch({ type: 'fileFormat', payload: '0' });
  };

  useEffect(() => {
    optionsDispatch({ type: 'amoutNumber', payload: defaultSelectTop });
  }, [defaultSelectTop]);

  return (
    <Modal
      className="dbi-download-type dbi-download-type-two"
      visible={isOpen}
      size={820}
      caption={
        <div className="download-head">
          <span>{formatMessage({ id: 'dbi_download_settings' })}</span>
          {/* 没有实际提示文案，info icon暂时隐藏 */}
          {/* <TipContext className="tip-text-hd" tipText="" /> */}
        </div>
      }
      onClose={() => {
        onClose();
        reset();
      }}
    >
      <Modal.Body>
        <Form className="dbi-download-form teaui-form-alignment">
          <Form.Item label={formatMessage({ id: 'dbi_platform' })} showStatusIcon={false}>
            <Checkbox.Group
              value={options.platform}
              onChange={(value) => {
                if (!value?.length) return;
                handleChange({ type: 'platform', payload: value });
              }}
            >
              {PLATFORM_MAP.map((item) => (
                <Checkbox key={item.value} name={item.value}>
                  {formatMessage({ id: item.text })}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
          <Form.Item className='form-item--top-18' label={formatMessage({ id: 'dbi_label_data_calculation' })} showStatusIcon={false}>
            <Form>
              <Form.Item label={formatMessage({ id: 'dbi_label_time_settings' })} showStatusIcon={false}>
                <RadioGroup
                  className="dbi-radio-group"
                  value={options.timeSettings}
                  onChange={(value) => handleChange({ type: 'timeSettings', payload: value })}
                >
                  {TIME_SETTINGS_MAP.map((item) => (
                    <Radio key={item.value} name={item.value}>
                      {formatMessage({ id: item.text })}
                    </Radio>
                  ))}
                </RadioGroup>
              </Form.Item>
              <Form.Item label={formatMessage({ id: 'dbi_global_view_market_settings' })} showStatusIcon={false}>
                <Checkbox.Group
                  value={options.marketSettings}
                  onChange={(value) => {
                    if (!value?.length) return;
                    handleChange({ type: 'marketSettings', payload: value });
                  }}
                >
                  {MARKET_SETTINGS_MAP.map((item) => (
                    <Checkbox key={item.value} name={item.value} disabled={getMarketDisable(item.value)}>
                      {formatMessage({ id: item.text })}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
              {isDodHide || (
                <Form.Item label={formatMessage({ id: 'dbi_label_ratio' })} showStatusIcon={false}>
                  <Switch
                    value={options.ratio}
                    onClick={() => handleChange({ type: 'ratio', payload: !options.ratio })}
                  />
                </Form.Item>
              )}
            </Form>
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbi_label_other_settings' })} showStatusIcon={false}>
            <RadioGroup
              value={options.selectTop}
              onChange={(value) => handleChange({ type: 'selectTop', payload: value })}
            >
              {OHTER_SETTINGS_MAP.map((item) => (
                <Radio key={item.value} name={item.value}>
                  {formatMessage({ id: item.text })}
                </Radio>
              ))}
            </RadioGroup>
            {options.selectTop === '1' && (
              <InputNumber
                className='dbi-input-number'
                min={0}
                max={total}
                value={options.amoutNumber}
                onChange={(value) => {
                  handleChange({ type: 'amoutNumber', payload: value });
                }}
                onFocus={() => { }}
                onBlur={() => { }}
              />
            )}
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbi_label_file_format' })} showStatusIcon={false}>
            <RadioGroup
              value={options.fileFormat}
              onChange={(value) => handleChange({ type: 'fileFormat', payload: value })}
            >
              {FILE_FORMAT_MAP.map((item) => (
                <Radio key={item.value} name={item.value}>
                  {item.text}
                </Radio>
              ))}
            </RadioGroup>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          loading={isDownloading}
          disabled={!options?.platform?.length || !options?.marketSettings?.length}
          onClick={() => {
            download();
          }}
        >
          {formatMessage({ id: 'dbi_confirm' })}
        </Button>
        <Button
          type="weak"
          onClick={() => {
            onClose();
            reset();
          }}
        >
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
